import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Button, useToggle } from '@factorialco/deprecated-design-system'
import { ActionsDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { BecomeModal } from 'modules/authentication/public'
import { ActionsEmployeeData } from 'modules/employees/components/Actions/types'
import useShowBecomeHint from 'modules/employees/hooks/useShowBecomeHint'
import { getEmployeeJobRole } from 'modules/job_catalog/lib/utils'

import ActivateModal from './ActivateModal'
import BecomeHint from './BecomeHint'
import DisableMfaModal from './DisableMfaModal'
import SetWelcomeSpaceAccessModal from './SetWelcomeSpaceAccessModal'
import UninviteModal from './UninviteModal'
import useOptions from './useOptions'

interface Props {
  employeeData: ActionsEmployeeData
}

const Actions = ({ employeeData }: Props) => {
  const { pathname } = useLocation()
  const [openBecome, setOpenBecome] = React.useState(false)
  const [openDisableMfa, setOpenDisableMfa] = React.useState(false)
  const [openUninvite, toggleUninvite] = useToggle(false)
  const [openSetWelcomeSpaceAccess, toggleSetWelcomeSpaceAccess] = useToggle(false)
  const [openActivate, toggleActivate] = useToggle(false)
  const { shouldShowHint, onCloseHint } = useShowBecomeHint()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  // TODO: Move this to the root page query
  const { data, loading } = useQuery(ActionsDocument, {
    variables: { companyId: employeeData.company.id, employeeId: employeeData.id },
  })

  const features = data?.features?.featuresConnection?.nodes ?? []
  const hasEmployeeOnboarding =
    features.find((feature) => feature.name === 'employee_onboarding')?.enabled ?? false

  const onClickTerminate = () => {
    track(trackingEvents.employee.termination.terminateStart, {
      company_id: employeeData.company.id,
      user_id: employeeData.id,
      user_role: getEmployeeJobRole(employeeData?.referenceContract),
      time_stamp: Date.now(),
    })
    navigate(navigation.employees.id.terminate.fullPath({ id: employeeData.id }))
  }

  const options = useOptions({
    employeeData,
    onClickUninvite: toggleUninvite(true),
    onClickBecome: () => {
      track(trackingEvents.employee.become.startBecome, { from: pathname })
      onCloseHint()
      setOpenBecome(true)
    },
    onClickDisableMfa: () => {
      setOpenDisableMfa(true)
    },
    onClickTerminate: () => onClickTerminate(),
    onClickSetWelcomeSpaceAccess: toggleSetWelcomeSpaceAccess(true),
    onClickActivate: toggleActivate(true),
    queryData: data,
  })

  if (!options.length || loading) return null

  return (
    <>
      <BecomeHint onClose={onCloseHint} open={shouldShowHint}>
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button type="secondary" onClick={onCloseHint} iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="center">
            {options.map(({ title, description, onClick, danger }, index) => (
              <PopoverMenu.TouchableOption
                key={index}
                danger={danger}
                title={title}
                onClick={onClick}
                description={description}
              />
            ))}
          </PopoverMenu.Content>
        </PopoverMenu>
      </BecomeHint>
      {openUninvite && (
        <UninviteModal accessId={employeeData.access.id} onClose={toggleUninvite(false)} />
      )}
      {openBecome && (
        <BecomeModal
          accessId={employeeData.access.id}
          firstName={employeeData.firstName}
          onClose={() => setOpenBecome(false)}
        />
      )}
      {openDisableMfa && (
        <DisableMfaModal
          accessId={employeeData.access.id}
          onClose={() => setOpenDisableMfa(false)}
        />
      )}
      {openSetWelcomeSpaceAccess && (
        <SetWelcomeSpaceAccessModal
          employeeId={employeeData.id}
          onClose={toggleSetWelcomeSpaceAccess(false)}
        />
      )}
      {openActivate && (
        <ActivateModal
          employeeData={employeeData}
          hasEmployeeOnboarding={hasEmployeeOnboarding}
          onClose={toggleActivate(false)}
        />
      )}
    </>
  )
}

export default observer(Actions)
